import type { ReactNode } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { SectionLabel, Switch } from '@kula-ai/components';

export type EmailBlockSectionProps = {
  title: string;
  description?: string;
  /** Anchor for `scrollIntoView` when validating (e.g. `interview-comm-candidate-email`). */
  sectionId?: string;
  /** When true, shows a switch at the end of the section divider (via SectionLabel `trailing`). */
  showSwitch?: boolean;
  switchChecked?: boolean;
  onSwitchChange?: (_next: boolean) => void;
  /** Extra node after the switch on the same row (rare). */
  trailingSlot?: ReactNode;
  children?: ReactNode;
  className?: string;
};

/**
 * Reusable header for interview / ATS communication blocks: Kula `SectionLabel` divider
 * with optional `Switch`. Content stacks below.
 */
export const EmailBlockSection = ({
  title,
  description,
  sectionId,
  showSwitch = false,
  switchChecked = false,
  onSwitchChange,
  trailingSlot,
  children,
  className,
}: EmailBlockSectionProps) => {
  const trailing = (
    <Flex alignItems="center" gap="2">
      {showSwitch ? (
        <Switch
          checked={switchChecked}
          onCheckedChange={(next) => {
            onSwitchChange?.(next);
          }}
          aria-label={title}
        />
      ) : null}
      {trailingSlot}
    </Flex>
  );

  return (
    <Box id={sectionId} className={className} w="100%" scrollMarginTop="4">
      <SectionLabel
        showDivider
        trailing={showSwitch || trailingSlot ? trailing : undefined}
      >
        {title}
      </SectionLabel>
      {description ? (
        <Text fontSize="sm" color="secondary-700" mt="2" mb="3">
          {description}
        </Text>
      ) : null}
      {children ? <Box mt="4">{children}</Box> : null}
    </Box>
  );
};
